import { config } from "../config";
import { ApiError } from "./errors";
import { callGateway, type CallerIdentity } from "./gateway";

/**
 * One subscriber's own RADIUS attributes (owner 2026-10-29, radaccess v1.216):
 *   GET  /access/operator/{ns}/subscribers/{AccountID}/radius-attributes
 *   GET  .../radius-attributes/history
 *   POST .../radius-attributes {username, changes[], reason, accessToken}
 * Owner and superadmin only. A fresh SMS code to the caller's own mobile
 * comes first; every change is recorded with its reason, and an email goes to
 * the caller's contact email. The plan's own attributes are read-only here.
 */

export type RadiusTable = "check" | "reply";
export type RadiusAction = "add" | "change" | "delete";

export interface RadiusAttr {
  id?: number;
  attribute: string;
  op: string;
  value: string;
  /** Password items: listed without value, never edited here. */
  locked?: boolean;
}

export interface RadiusLoginAttrs {
  username: string;
  check: RadiusAttr[];
  reply: RadiusAttr[];
  plan?: string;
  planCheck: RadiusAttr[];
  planReply: RadiusAttr[];
}

export interface RadiusAttrChangeIn {
  table: RadiusTable;
  action: RadiusAction;
  id?: number | undefined;
  attribute?: string | undefined;
  op?: string | undefined;
  value?: string | undefined;
}

export interface RadiusAttrChange {
  table: RadiusTable;
  action: RadiusAction;
  rowId: number;
  attribute: string;
  oldOp?: string;
  oldValue?: string;
  newOp?: string;
  newValue?: string;
}

export interface RadiusAttrChangeRecord extends RadiusAttrChange {
  username: string;
  reason: string;
  actor: string;
  actorRole: string;
  changeSet: string;
  at: string;
}

const base = (ns: string, accountId: string) => `/operator/${encodeURIComponent(ns)}/subscribers/${encodeURIComponent(accountId)}/radius-attributes`;

export async function getRadiusAttrs(identity: CallerIdentity, ns: string, accountId: string, signal?: AbortSignal): Promise<RadiusLoginAttrs[]> {
  const r = await callGateway<{ logins?: RadiusLoginAttrs[] }>(identity, config().gatewayBase, base(ns, accountId), signal ? { signal } : {});
  return (r.data?.logins ?? []).map((l) => ({ ...l, check: l.check ?? [], reply: l.reply ?? [], planCheck: l.planCheck ?? [], planReply: l.planReply ?? [] }));
}

export async function getRadiusAttrHistory(identity: CallerIdentity, ns: string, accountId: string, signal?: AbortSignal): Promise<RadiusAttrChangeRecord[]> {
  const r = await callGateway<{ changes?: RadiusAttrChangeRecord[] }>(identity, config().gatewayBase, `${base(ns, accountId)}/history`, signal ? { signal } : {});
  return r.data?.changes ?? [];
}

export async function changeRadiusAttrs(
  identity: CallerIdentity,
  ns: string,
  accountId: string,
  body: { username: string; changes: RadiusAttrChangeIn[]; reason: string; accessToken: string },
): Promise<{ changeSet: string; changes: RadiusAttrChange[] }> {
  const r = await callGateway<{ changeSet: string; changes: RadiusAttrChange[] }>(identity, config().gatewayBase, base(ns, accountId), { method: "POST", body });
  return { changeSet: r.data?.changeSet ?? "", changes: r.data?.changes ?? [] };
}

/** FreeRADIUS operators the server accepts, per item kind. */
export const RADIUS_OPS: Record<RadiusTable, string[]> = {
  check: [":=", "==", "+=", "!=", ">", ">=", "<", "<=", "=~", "!~", "=*", "!*"],
  reply: [":=", "=", "+="],
};

const NAME = /^[A-Za-z][A-Za-z0-9-]{0,63}$/;

export const isPasswordAttr = (a: string) => {
  const l = a.trim().toLowerCase();
  return l.endsWith("-password") || l === "password";
};

/** Why a change cannot be sent, or "" (mirrors radaccess model.ValidateRadiusAttrChange). */
export function radiusChangeProblem(c: RadiusAttrChangeIn): string {
  if (c.action === "delete") return c.id ? "" : "Choose the row to delete.";
  const a = c.attribute ?? "";
  if (!NAME.test(a)) return "Attribute name: letters, digits and dashes, starting with a letter.";
  if (isPasswordAttr(a)) return "Passwords are changed from Reset password, not here.";
  if (!RADIUS_OPS[c.table].includes(c.op ?? "")) return `Operator ${c.op || "(none)"} is not allowed on a ${c.table} item.`;
  const v = c.value ?? "";
  if (!v || v.length > 253 || v.trim() !== v) return "Value: 1 to 253 characters, no spaces at the ends.";
  if ([...v].some((ch) => ch.charCodeAt(0) < 0x20 || ch.charCodeAt(0) === 0x7f)) return "Value has a control character.";
  return "";
}

/** One line for a change, as the list and the email say it. */
export function radiusChangeLine(c: RadiusAttrChangeIn & { oldOp?: string | undefined; oldValue?: string | undefined }, before?: RadiusAttr): string {
  const kind = c.table === "check" ? "check" : "reply";
  const old = before ?? (c.oldOp !== undefined ? { attribute: c.attribute ?? "", op: c.oldOp, value: c.oldValue ?? "" } : undefined);
  switch (c.action) {
    case "add":
      return `Add (${kind}): ${c.attribute} ${c.op} ${c.value}`;
    case "change":
      return `Change (${kind}): ${old ? `${old.attribute} ${old.op} ${old.value}` : `row ${c.id}`} → ${c.attribute} ${c.op} ${c.value}`;
    default:
      return `Delete (${kind}): ${old ? `${old.attribute} ${old.op} ${old.value}` : `row ${c.id}`}`;
  }
}

/** A stored change, one line. */
export function radiusRecordLine(r: RadiusAttrChangeRecord): string {
  return radiusChangeLine(
    { table: r.table, action: r.action, id: r.rowId, attribute: r.attribute, op: r.newOp, value: r.newValue },
    r.action === "add" ? undefined : { attribute: r.attribute, op: r.oldOp ?? "", value: r.oldValue ?? "" },
  );
}

/** What the Owner is told when a change is refused. */
export function radiusAttrErrorText(err: unknown): string {
  if (err instanceof ApiError) {
    const b = (err.body ?? {}) as { error?: unknown };
    const code = typeof b.error === "string" ? b.error : "";
    if (err.status === 401 && code === "widget_token_rejected") return "The code is not right or has expired. Send a new code.";
    if (err.status === 403) return "Only the operator's Owner or superadmin can change RADIUS attributes.";
    if (err.status === 404) return "That row changed meanwhile. Reload and try again.";
    if (code === "reason_required") return "Type the reason (at least 5 characters).";
    if (code === "account_email_unverified") return "Confirm your contact email first (My contact details): the change notice goes there.";
    if (code === "no_mobile") return "Add your mobile in My contact details first: the code goes there.";
    if (code === "notice_unavailable") return "The notice email could not be sent, so nothing was changed. Try again in a few minutes.";
    if (code === "radius_attributes_unavailable") return "RADIUS attribute changes are not switched on yet.";
    if (err.status === 429) return "Too many attempts. Wait and try again.";
    if (code) return code;
  }
  return err instanceof Error ? err.message : String(err);
}
